import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Navigate, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
	PortalCentered,
	portalButton,
	portalHeading,
	portalInput,
	portalLabel,
	portalPanel,
} from '@/routes/appointments/portal-shell'
import { trpc } from '@/trpc/client'

const PatientLoginSchema = z.object({
	email: z.string().email('Neplatná e-mailová adresa'),
	personalId: z
		.string()
		.regex(/^\d{6}\/?\d{3,4}$/, 'Neplatný formát rodného čísla'),
})

type PatientLoginFormData = z.infer<typeof PatientLoginSchema>

// The credential message must only appear for an actual credential rejection —
// a server outage rendered as "wrong rodné číslo" sends users retrying into
// the rate limiter and generates false support calls.
function loginErrorMessage(code: string | undefined): string {
	switch (code) {
		case 'UNAUTHORIZED':
			return 'Nesprávný e-mail nebo rodné číslo'
		case 'TOO_MANY_REQUESTS':
			return 'Příliš mnoho pokusů o přihlášení. Zkuste to prosím později.'
		default:
			return 'Přihlášení se nezdařilo. Zkuste to prosím později.'
	}
}

export function PatientLoginPage() {
	const navigate = useNavigate()
	const utils = trpc.useUtils()
	const [sessionError, setSessionError] = useState<string | null>(null)

	// Already logged in → straight to the viewer
	const { data: me } = trpc.patientPortal.me.useQuery(undefined, {
		retry: false,
	})

	const login = trpc.patientPortal.login.useMutation({
		onSuccess: async ({ patientId }) => {
			// Verify the session cookie actually landed before navigating —
			// otherwise a dropped Set-Cookie (e.g. Secure cookie over plain HTTP)
			// would bounce the guard straight back here with zero feedback.
			await utils.patientPortal.me.invalidate()
			try {
				await utils.patientPortal.me.fetch()
				navigate(`/appointments/${patientId}`, { replace: true })
			} catch {
				setSessionError(
					'Přihlášení se nezdařilo — relace nebyla uložena. Zkuste to prosím znovu.',
				)
			}
		},
		onMutate: () => setSessionError(null),
	})

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<PatientLoginFormData>({
		resolver: zodResolver(PatientLoginSchema),
	})

	const onSubmit = (data: PatientLoginFormData) => {
		login.mutate(data)
	}

	if (me) {
		return <Navigate to={`/appointments/${me.id}`} replace />
	}

	return (
		<PortalCentered>
			<div className={`${portalPanel} w-full max-w-md p-8 sm:p-10`}>
				<div className="mb-8 text-center">
					<span className={portalLabel}>Pacientský portál</span>
					<h1 className={`${portalHeading} mt-2 text-[28px] leading-tight`}>
						Moje objednávky
					</h1>
					<div className="mx-auto mt-4 h-[2px] w-[55px] bg-[#d7ad6c]" />
					<p className="mt-4 text-base">
						Přihlaste se pro zobrazení vašich termínů
					</p>
				</div>

				<form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
					<div className="space-y-2">
						<Label
							htmlFor="email"
							className="font-[Poppins,sans-serif] font-medium text-[#242424]"
						>
							E-mail
						</Label>
						<Input
							id="email"
							type="email"
							autoComplete="email"
							placeholder="vas@email.cz"
							className={portalInput}
							{...register('email')}
						/>
						{errors.email && (
							<p className="text-destructive text-sm">{errors.email.message}</p>
						)}
					</div>

					<div className="space-y-2">
						<Label
							htmlFor="personalId"
							className="font-[Poppins,sans-serif] font-medium text-[#242424]"
						>
							Rodné číslo
						</Label>
						<Input
							id="personalId"
							type="text"
							inputMode="numeric"
							autoComplete="off"
							placeholder="YYMMDD/XXXX"
							className={portalInput}
							{...register('personalId')}
						/>
						{errors.personalId && (
							<p className="text-destructive text-sm">
								{errors.personalId.message}
							</p>
						)}
					</div>

					{login.isError && (
						<p className="text-destructive text-sm">
							{loginErrorMessage(login.error.data?.code)}
						</p>
					)}
					{sessionError && (
						<p className="text-destructive text-sm">{sessionError}</p>
					)}

					<Button
						type="submit"
						className={`${portalButton} h-11 w-full`}
						disabled={login.isPending}
					>
						{login.isPending ? 'Přihlašování…' : 'Přihlásit se'}
					</Button>
				</form>
			</div>
			<p className="text-sm text-[#919194]">
				Přístup pouze pro pacienty ordinace
			</p>
		</PortalCentered>
	)
}
